package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.ijoin.data.api.ApiClient
import com.ijoin.data.repository.AuthRepository
import com.ijoin.getPlatform
import com.ijoin.i18n.strings
import com.ijoin.deeplink.DeepLinkHandler
import com.ijoin.platform.openUrl
import com.ijoin.platform.redirectToUrl
import com.ijoin.platform.startOAuthFlow
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardInputCorner
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.EventTextAction
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.design.eventOutlinedFieldColors
import com.ijoin.ui.test.TestTags
import com.ijoin.ui.util.buildAppleAuthStartUrl
import com.ijoin.ui.util.buildGoogleAuthStartUrl
import com.ijoin.analytics.AnalyticsTracker
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LoginScreen(
    onLoginSuccess: () -> Unit,
    onNavigateToRegister: () -> Unit,
    onNavigateToPasswordReset: () -> Unit,
    authRepository: AuthRepository = remember { AuthRepository() }
) {
    val strings = strings()
    val scope = rememberCoroutineScope()
    val platform = remember { getPlatform() }

    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var passwordVisible by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(strings.login) },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = EventCardBackground,
                    titleContentColor = EventCardAccentDark
                )
            )
        },
        containerColor = EventCardBackground
    ) { paddingValues ->
        val focusManager = LocalFocusManager.current
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(eventBackgroundBrush())
                .padding(paddingValues)
                .padding(16.dp)
                .pointerInput(Unit) {
                    detectTapGestures(onTap = { focusManager.clearFocus() })
                },
            contentAlignment = Alignment.Center
        ) {
            EventSurfaceCard(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(max = 420.dp),
                contentPadding = PaddingValues(24.dp)
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Text(
                        text = strings.welcomeBack,
                        style = MaterialTheme.typography.headlineMedium,
                        textAlign = TextAlign.Center
                    )

                    OutlinedTextField(
                        value = email,
                        onValueChange = {
                            email = it
                            errorMessage = null
                        },
                        label = { Text(strings.email) },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                        modifier = Modifier
                            .fillMaxWidth()
                            .testTag(TestTags.LoginScreen.EMAIL_FIELD),
                        enabled = !isLoading,
                        shape = RoundedCornerShape(EventCardInputCorner),
                        colors = eventOutlinedFieldColors()
                    )

                    OutlinedTextField(
                        value = password,
                        onValueChange = {
                            password = it
                            errorMessage = null
                        },
                        label = { Text(strings.password) },
                        singleLine = true,
                        visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                        trailingIcon = {
                            IconButton(onClick = { passwordVisible = !passwordVisible }) {
                                Icon(
                                    imageVector = if (passwordVisible) Icons.Filled.Visibility else Icons.Filled.VisibilityOff,
                                    contentDescription = if (passwordVisible) "Hide password" else "Show password"
                                )
                            }
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .testTag(TestTags.LoginScreen.PASSWORD_FIELD),
                        enabled = !isLoading,
                        shape = RoundedCornerShape(EventCardInputCorner),
                        colors = eventOutlinedFieldColors()
                    )

                    EventTextAction(
                        text = strings.forgotPassword,
                        onClick = onNavigateToPasswordReset,
                        enabled = !isLoading,
                        modifier = Modifier.align(Alignment.End)
                    )

                    if (errorMessage != null) {
                        Text(
                            text = errorMessage.orEmpty(),
                            color = EventCardAlert,
                            style = MaterialTheme.typography.bodySmall,
                            textAlign = TextAlign.Center
                        )
                    }

                    EventPrimaryButton(
                        text = strings.login,
                        onClick = {
                            if (email.isBlank() || password.isBlank()) {
                                errorMessage = strings.fillAllFields
                                return@EventPrimaryButton
                            }

                            if (!email.contains("@")) {
                                errorMessage = strings.invalidEmail
                                return@EventPrimaryButton
                            }

                            isLoading = true
                            scope.launch {
                                authRepository.login(email, password)
                                    .onSuccess {
                                        isLoading = false
                                        onLoginSuccess()
                                    }
                                    .onFailure { error ->
                                        isLoading = false
                                        errorMessage = error.message ?: strings.loginError
                                    }
                            }
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(50.dp)
                            .testTag(TestTags.LoginScreen.SUBMIT_BUTTON),
                        enabled = !isLoading,
                        isLoading = isLoading
                    )

                    EventOutlineButton(
                        text = strings.continueWithGoogle,
                        onClick = {
                            AnalyticsTracker.trackCompleteRegistration("google_login")
                            val url = buildGoogleAuthStartUrl(ApiClient.getBaseUrl(), platform.isMobile)
                            if (platform.isMobile) {
                                startOAuthFlow(url, "ijoin") { callbackUrl ->
                                    callbackUrl?.let { DeepLinkHandler.handleUrl(it) }
                                }
                            } else {
                                redirectToUrl(url)
                            }
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(50.dp)
                            .testTag(TestTags.LoginScreen.GOOGLE_BUTTON),
                        enabled = !isLoading
                    )

                    EventOutlineButton(
                        text = strings.continueWithApple,
                        onClick = {
                            AnalyticsTracker.trackCompleteRegistration("apple_login")
                            val url = buildAppleAuthStartUrl(ApiClient.getBaseUrl(), platform.isMobile)
                            if (platform.isMobile) {
                                startOAuthFlow(url, "ijoin") { callbackUrl ->
                                    callbackUrl?.let { DeepLinkHandler.handleUrl(it) }
                                }
                            } else {
                                redirectToUrl(url)
                            }
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(50.dp)
                            .testTag(TestTags.LoginScreen.APPLE_BUTTON),
                        enabled = !isLoading
                    )

                    Row(
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = strings.dontHaveAccount,
                            style = MaterialTheme.typography.bodyMedium
                        )
                        Spacer(modifier = Modifier.width(4.dp))
                        EventTextAction(
                            text = strings.registerHere,
                            onClick = onNavigateToRegister,
                            modifier = Modifier.testTag(TestTags.LoginScreen.REGISTER_NAV_BUTTON)
                        )
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    // App store badges (using AsyncImage to avoid Skia decoder issues on Web)
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        AsyncImage(
                            model = "${ApiClient.getBaseUrl()}/api/media/common/badge_app_store.png",
                            contentDescription = strings.downloadOnAppStore,
                            modifier = Modifier
                                .height(40.dp)
                                .clickable { openUrl("https://apps.apple.com/us/app/ijoinapp/id6758534708") },
                            contentScale = ContentScale.Fit
                        )
                        AsyncImage(
                            model = "${ApiClient.getBaseUrl()}/api/media/common/badge_google_play.png",
                            contentDescription = strings.getItOnGooglePlay,
                            modifier = Modifier
                                .height(40.dp)
                                .clickable { openUrl("https://play.google.com/store/apps/details?id=com.ijoin") },
                            contentScale = ContentScale.Fit
                        )
                    }
                }
            }
        }
    }
}
